/**
 * A breadcrumb that survives the bank redirect.
 *
 * Connecting a bank navigates the whole page away (on web — see
 * services/bankLinkService). Everything in memory dies, including the onboarding
 * store, so returning users land on a cold boot with no idea they were halfway
 * through first-run. This is the note we leave ourselves.
 *
 * Two things about it are load-bearing:
 *
 * 1. It must be readable SYNCHRONOUSLY, before Vue mounts. The gate it feeds
 *    (needsOnboarding) is otherwise racing the data it's gating on — see the
 *    comment there.
 *
 * 2. localStorage, not sessionStorage — even though the bank callback capture
 *    next door uses sessionStorage. That one only has to survive a same-tab
 *    redirect. This has to survive a trip through BankID, which can come back in
 *    a different browsing context, and an installed PWA and Safari have separate
 *    storage partitions for the same origin on iOS. The TTL and the user stamp
 *    are what make the wider scope safe.
 *
 * The snapshot deliberately holds no derived data — only where we were. The
 * findings are re-fetchable from the (idempotent) bootstrap endpoint, so there's
 * no stale blob to reconcile.
 */

import { logger } from "../utils/logger"

const KEY = "budget-app-onboarding-resume"

// Bump when the shape changes; an unrecognised version is discarded rather than
// migrated. A stale blob is worth nothing — the user just sees first-run again.
const VERSION = 1

// Long enough for a slow BankID round trip on a bad connection, short enough
// that an abandoned attempt doesn't ambush the user with onboarding days later.
const TTL_MS = 30 * 60 * 1000

/** Where in the bank flow we were when the page went away. */
export type BankPhase = "connecting" | "selecting" | "deriving" | "review"

export interface OnboardingSnapshot {
  v: number
  /** Guards against restoring one user's progress into another's session. */
  userId: string
  bankPhase: BankPhase
  connectionId: string | null
  /** Epoch ms, for the TTL. */
  startedAt: number
}

function isFresh(snap: OnboardingSnapshot): boolean {
  const age = Date.now() - snap.startedAt
  // A negative age means the clock moved backwards (or the blob was tampered
  // with); treat it as unusable rather than valid forever.
  return age >= 0 && age < TTL_MS
}

/**
 * Read the snapshot, or null if there isn't a usable one.
 *
 * Anything malformed, outdated, or expired is treated as absent — this runs at
 * startup before anything else, so it must never throw.
 */
export function readResume(): OnboardingSnapshot | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as OnboardingSnapshot
    if (parsed?.v !== VERSION || !parsed.userId || !isFresh(parsed)) {
      localStorage.removeItem(KEY)
      return null
    }
    return parsed
  } catch (error) {
    logger.warn("Could not read onboarding resume snapshot", { error })
    return null
  }
}

export function writeResume(snap: Omit<OnboardingSnapshot, "v" | "startedAt">): void {
  try {
    const payload: OnboardingSnapshot = { ...snap, v: VERSION, startedAt: Date.now() }
    localStorage.setItem(KEY, JSON.stringify(payload))
  } catch (error) {
    // Private mode / quota. Losing the breadcrumb costs the user a re-run of
    // onboarding, not their data, so it isn't worth failing the flow over.
    logger.warn("Could not persist onboarding resume snapshot", { error })
  }
}

export function clearResume(): void {
  try {
    localStorage.removeItem(KEY)
  } catch (error) {
    logger.warn("Could not clear onboarding resume snapshot", { error })
  }
}
